import React, { useState } from "react";
import PageTitle from "../../common/PageTitle";
import Form from "../../common/forms/Form";
import FormInput from "../../common/forms/FormInput";
import NotificationModal from "../../common/settings-modal/NotificationModal";
import RequestCompleteNotice from "../../common/settings-modal/RequestCompleteNotice";
import AgreementCheckBox from "../../common/forms/AgreementCheckBox";
import { ValType } from "../../utils/ValidationFunctions";
import { genContactCategoryOptionArray } from "../../utils/ContactCategory";
import { RequestState } from "../../utils/RequestState";
import styles from "./ContactPage.module.scss";
import SonicLink from "../../common/routing/SonicLink";

type Props = {
  requestState: RequestState;
  error: boolean;
  setName: React.Dispatch<React.SetStateAction<string>>;
  setEmail: React.Dispatch<React.SetStateAction<string>>;
  setCategory: React.Dispatch<React.SetStateAction<string>>;
  setMessage: React.Dispatch<React.SetStateAction<string>>;
  handleSubmit: () => void;
};

const ContactPage: React.FC<Props> = ({
  requestState,
  error,
  setName,
  setEmail,
  setCategory,
  setMessage,
  handleSubmit,
}) => {
  const [isAgreement, setIsAgreement] = useState<boolean>(false);

  const modalColor = () => {
    switch (requestState) {
      case RequestState.Failed:
        return "#ff4500";
      case RequestState.Doing:
        return "#f79b00";
      default:
        return "";
    }
  };

  const modalMessage = () => {
    switch (requestState) {
      case RequestState.Failed:
        return "送信に失敗しました";
      case RequestState.Doing:
        return "送信中・・・";
      default:
        return "";
    }
  };

  return (
    <div className={styles["f-contact"]}>
      <PageTitle title="CONTACT" subtext="お問い合わせ" />

      {requestState === RequestState.Success ? (
        <RequestCompleteNotice
          handler={() => {
            history.back();
          }}
          title="お問い合わせ完了"
          text={`お問い合わせいただきありがとうございます。\n後ほど担当者よりご連絡させていただきますので、今しばらくお待ちください。`}
          sub_text={`※数日お時間を頂戴する場合がございます。\n予めご了承くださいますようお願いいたします。`}
          displayImage={false}
        />
      ) : (
        <div className={styles["f-contact__wrapper"]}>
          {/* 夏季休暇、年末年始休暇などの HolidayAnnounce はここに追加する。コンポーネント有。 */}
          <p>
            SELeCTに対するお問い合わせは、下記お問い合わせフォームよりご連絡ください。
            <br />
            ご入力いただいた情報は「
            <SonicLink to="/info/privacy/" newTab={true}>
              プライバシーポリシー
            </SonicLink>
            」に基づく厳重な管理を行い、当方針に規定された条件に該当する場合を除いて第三者に開示することは一切ありません。
          </p>
          <div className={styles["f-contact__attention"]}>
            <span>{`【お問い合わせフォームに関する注意事項】\n`}</span>
            お問い合わせの内容によってはお応えできない場合、またはお時間をいただく場合がございます。ご了承ください。
          </div>
          <div className={styles["f-contact__guide"]}>
            企業様はお問い合わせ本文に下記をご入力いただくとスムーズに進めることが出来ます。
            <br />
            <br />
            <span>{`■お打ち合わせ希望日時\n`}</span>
            ※第3希望までご入力いただけますとスケジューリングがスムーズです。
            <br />
            【第1希望】
            <br />
            【第2希望】
            <br />
            【第3希望】
            <br />
            <br />
            <span>{`■お打ち合わせ方法\n`}</span>
            ※下記からご選択いただけますとスムーズです。
            <br />
            【Zoom / Discord / Google Meet / オフライン(弊社への来社)】
            <br />
            <br />
            <span>{`■内容\n`}</span>
            ※現時点で考えている範囲の詳細をご記入頂けますとスムーズに進めることが出来ます。
          </div>
          <Form handler={handleSubmit}>
            <div className={styles["f-contact__inner"]}>
              <div className={styles["f-contact__form"]}>
                <FormInput
                  type="text"
                  label="お名前"
                  label_class={`${styles["f-contact__form-title"]} ${styles["required-mark"]}`}
                  name="name"
                  style_class={styles["f-contact__form-input"]}
                  handler={(value) => {
                    if (value !== undefined && typeof value === "string") {
                      setName(value);
                    }
                  }}
                  val_type={ValType.NotNull}
                />
              </div>
              <div className={styles["f-contact__form"]}>
                <FormInput
                  type="text"
                  label="メールアドレス"
                  label_class={`${styles["f-contact__form-title"]} ${styles["required-mark"]}`}
                  name="email"
                  style_class={styles["f-contact__form-input"]}
                  handler={(value) => {
                    if (value !== undefined && typeof value === "string") {
                      setEmail(value);
                    }
                  }}
                  val_type={ValType.EmailFormat}
                />
              </div>
              <div className={styles["f-contact__form"]}>
                <FormInput
                  type="select"
                  label="お問い合わせ内容"
                  label_class={`${styles["f-contact__form-title"]} ${styles["required-mark"]}`}
                  name="title"
                  select_class={styles["f-contact__form-input"]}
                  holder_class={styles["f-contact__form-input-novalue"]}
                  handler={(value) => {
                    if (value !== undefined && typeof value === "string") {
                      setCategory(value);
                    }
                  }}
                  options={genContactCategoryOptionArray()}
                  val_type={ValType.NotNull}
                />
              </div>
              <div className={styles["f-contact__form"]}>
                <FormInput
                  type="textarea"
                  label="お問い合わせ本文"
                  label_class={`${styles["f-contact__form-title"]} ${styles["required-mark"]}`}
                  name="message"
                  style_class={styles["f-contact__form-input"]}
                  rows={5}
                  handler={(value) => {
                    if (value !== undefined && typeof value === "string") {
                      setMessage(value);
                    }
                  }}
                  val_type={ValType.NotNull}
                />
              </div>
              <AgreementCheckBox
                value={isAgreement}
                handler={() => setIsAgreement(!isAgreement)}
              >
                <SonicLink to="/info/privacy/" newTab={true}>
                  プライバシーポリシー
                </SonicLink>
                に同意して送信
              </AgreementCheckBox>
            </div>
          </Form>
          {error ? (
            <div className={styles["f-contact__error"]}>
              必須項目が入力されていません
            </div>
          ) : null}
          <div className={styles["f-contact__btn"]}>
            <button
              type="button"
              disabled={isAgreement === true ? false : true}
              onClick={handleSubmit}
            >
              送信
            </button>
          </div>
        </div>
      )}
      {requestState !== RequestState.Waiting ? (
        <NotificationModal
          color={modalColor()}
          icon_class={
            requestState === RequestState.Failed
              ? ["fal", "times-circle"]
              : undefined
          }
          message={modalMessage()}
          type="contact"
        />
      ) : null}
    </div>
  );
};

export default ContactPage;
